import { hostname } from '@/api/client';
import translator from '@/locale/translator';
import type { IBanner } from '@/types/banner';

/** Maps render_video.data.status to overlay props for AdImagePicker.
 * Mirrors the video overlay logic in BannerPreview.vue:
 * status 1/6/7 → ready (streamable), status < 0 → failed, anything else → processing.
 */
export function videoStatusOverlay(status: number | undefined): {
  overlayMessage: string | null;
  overlayError: boolean;
} {
  if (status === undefined || status === 1 || status === 6 || status === 7)
    return { overlayMessage: null, overlayError: false };
  if (status < 0) return { overlayMessage: translator.get('campaigns.media.video_failed'), overlayError: true };
  return { overlayMessage: translator.get('campaigns.media.video_processing'), overlayError: false };
}

/** Builds a video stream MP4 URL from a render_video.data.hash. */
export function videoStreamUrl(hash: string | undefined): string | null {
  return hash ? `${hostname}/v1/media/videos/${hash}/stream?extension=mp4` : null;
}

/** Returns overlay props for the banner image render state, or null if the image is ready.
 * Used as the first check before evaluating video status.
 */
export function bannerRenderOverlay(
  renderError: string | null,
  hasImageData: boolean
): { overlayMessage: string; overlayError: boolean } | null {
  if (renderError) return { overlayMessage: translator.get('campaigns.media.banner_failed'), overlayError: true };
  if (!hasImageData)
    return { overlayMessage: translator.get('campaigns.media.banner_processing'), overlayError: false };
  return null;
}

/** Returns the PickerImage-compatible state for a banner shown inside ImagePickerModal.
 * Uses the proxy thumbnail URL (smaller) rather than the jump URL used for selected-item display.
 */
export function bannerThumbnailState(banner: IBanner): {
  thumbnailUrl: string | null | undefined;
  videoUrl: string | null;
  overlayMessage: string | null;
  overlayError: boolean;
} {
  const renderOverlay = bannerRenderOverlay(banner.render_error, !!banner.render_image?.data);
  if (renderOverlay) return { thumbnailUrl: null, videoUrl: null, ...renderOverlay };
  const videoData = banner.render_video?.data;
  const overlay = videoStatusOverlay(videoData?.status);
  return {
    thumbnailUrl: banner.render_image?.data?.proxy?.url_thumbnail,
    videoUrl: !overlay.overlayMessage ? videoStreamUrl(videoData?.hash) : null,
    ...overlay,
  };
}

/** Returns the AdImagePicker prop set for a banner selected/displayed inline in the form.
 * Never shows overlay messages — just the image, and the video if it's ready.
 */
export function bannerDisplayState(banner: IBanner | null | undefined, imageId: number | null) {
  const imageUrl = imageId ? `${hostname}/v1/media/images/${imageId}/jump` : null;
  const videoData = banner?.render_video?.data;
  const videoUrl =
    videoData?.status === 1 || videoData?.status === 6 || videoData?.status === 7
      ? videoStreamUrl(videoData?.hash)
      : null;
  return { imageUrl, videoUrl, overlayMessage: null as string | null, overlayError: false };
}
